import React, { useState, useEffect } from "react";
import {
  Users,
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Eye,
  Ban,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Download,
  AlertTriangle,
  Star,
  MessageSquare,
  X,
  Save,
} from "lucide-react";
import AdminLayout from "../common/AdminLayout";
import Loader from "../common/Loader";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  address: string | null;
  parish: string | null;
  status: "active" | "inactive" | "blocked";
  email_verified: boolean;
  phone_verified: boolean;
  subscription_status: "free" | "premium";
  total_bookings: number;
  completed_bookings: number;
  total_inquiries: number;
  avg_rating: number | null;
  report_count: number;
  last_booking_date: string | null;
  created_at: string;
  updated_at: string;
}

interface CustomerFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  parish: string;
  address: string;
  status: "active" | "inactive" | "blocked";
  subscriptionStatus: "free" | "premium";
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

interface CustomerStats {
  total_clients: number;
  active_clients: number;
  inactive_clients: number;
  blocked_clients: number;
}

const CustomerManagement: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subscriptionFilter, setSubscriptionFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [stats, setStats] = useState<CustomerStats>({
    total_clients: 0,
    active_clients: 0,
    inactive_clients: 0,
    blocked_clients: 0,
  });
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<
    "view" | "create" | "edit" | "delete"
  >("view");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [formData, setFormData] = useState<CustomerFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    parish: "",
    address: "",
    status: "active",
    subscriptionStatus: "free",
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bulkAction, setBulkAction] = useState("");
  const [showBulkModal, setShowBulkModal] = useState(false);

  const { admin } = useAuth();

  // Create axios instance with auth token
  const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("skillseek_admin_token")}`,
    },
  });

  // Fetch clients from API
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/admin/clients", {
        params: {
          status: statusFilter !== "all" ? statusFilter : undefined,
          sort: "newest",
          page: currentPage,
          limit: 10,
        },
      });

      if (response.data.success) {
        setCustomers(response.data.clients);
        setPagination({
          currentPage: response.data.pagination.page,
          totalPages: response.data.pagination.totalPages,
          totalItems: response.data.pagination.totalItems,
          itemsPerPage: response.data.pagination.limit,
        });
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [statusFilter, currentPage]);

  // Handle customer selection
  const handleSelectCustomer = (customerId: number) => {
    setSelectedCustomers((prev) =>
      prev.includes(customerId)
        ? prev.filter((id) => id !== customerId)
        : [...prev, customerId],
    );
  };
  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      searchTerm === "" ||
      customer.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.phone || "").includes(searchTerm) ||
      (customer.parish || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSubscription =
      subscriptionFilter === "all" ||
      customer.subscription_status === subscriptionFilter;

    return matchesSearch && matchesSubscription;
  });

  const handleSelectAll = () => {
    if (selectedCustomers.length === customers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map((c) => c.id));
    }
  };

  // Open modal
  const openModal = (
    type: "view" | "create" | "edit" | "delete",
    customer?: Customer,
  ) => {
    setModalType(type);
    setSelectedCustomer(customer || null);
    setShowModal(true);
    setFormErrors({});

    if (type === "create") {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        parish: "",
        address: "",
        status: "active",
        subscriptionStatus: "free",
      });
    } else if (type === "edit" && customer) {
      setFormData({
        firstName: customer.first_name,
        lastName: customer.last_name,
        email: customer.email,
        phone: customer.phone || "",
        parish: customer.parish || "",
        address: customer.address || "",
        status: customer.status,
        subscriptionStatus: customer.subscription_status,
      });
    }
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedCustomer(null);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      parish: "",
      address: "",
      status: "active",
      subscriptionStatus: "free",
    });
    setFormErrors({});
  };

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) errors.firstName = "First name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last name is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = "Email is invalid";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const requestData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone || null,
        address: formData.address || null,
        parish: formData.parish || null,
        status: formData.status,
        subscription_status: formData.subscriptionStatus,
        password: modalType === "create" ? "password123" : undefined, // Default password for new customers
      };

      if (modalType === "create") {
        const response = await apiClient.post("/admin/clients", requestData);
        if (response.data.success) {
          await fetchCustomers();
          closeModal();
        }
      } else if (modalType === "edit" && selectedCustomer) {
        const response = await apiClient.put(
          `/admin/clients/${selectedCustomer.id}`,
          requestData,
        );
        if (response.data.success) {
          await fetchCustomers();
          closeModal();
        }
      }
    } catch (error: any) {
      console.error("Form submission error:", error);
      if (error.response?.data?.errors) {
        const backendErrors: { [key: string]: string } = {};
        error.response.data.errors.forEach((err: any) => {
          backendErrors[err.param] = err.msg;
        });
        setFormErrors(backendErrors);
      } else if (error.response?.data?.message) {
        setFormErrors({ general: error.response.data.message });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle customer deletion
  const handleDelete = async () => {
    if (!selectedCustomer) return;

    setIsSubmitting(true);

    try {
      const response = await apiClient.delete(
        `/admin/clients/${selectedCustomer.id}`,
      );
      if (response.data.success) {
        await fetchCustomers();
        closeModal();
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      if (error.response?.data?.message) {
        setFormErrors({ general: error.response.data.message });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle bulk actions
  const handleBulkAction = async () => {
    if (!bulkAction || selectedCustomers.length === 0) return;

    try {
      const actionMap: { [key: string]: string } = {
        active: "activate",
        inactive: "deactivate",
        blocked: "block",
      };

      const response = await apiClient.patch("/admin/clients/bulk", {
        clientIds: selectedCustomers,
        action: actionMap[bulkAction] || bulkAction,
      });

      if (response.data.success) {
        await fetchCustomers();
        setSelectedCustomers([]);
        setShowBulkModal(false);
        setBulkAction("");
      }
    } catch (error) {
      console.error("Bulk action error:", error);
    }
  };

  // Export customers
  const exportCustomers = () => {
    const csvContent = [
      [
        "ID",
        "First Name",
        "Last Name",
        "Email",
        "Phone",
        "Parish",
        "Status",
        "Subscription",
        "Service Inquiries",
        "Created At",
      ].join(","),
      ...customers.map((customer) =>
        [
          customer.id,
          customer.first_name,
          customer.last_name,
          customer.email,
          customer.phone || "",
          customer.parish || "",
          customer.status,
          customer.subscription_status,
          customer.total_inquiries || 0,
          new Date(customer.created_at).toLocaleDateString(),
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customers_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Helper functions
  const getStatusBadge = (status: string) => {
    const colors = {
      active: "badge-success",
      inactive: "badge-warning",
      blocked: "badge-danger",
    };
    return `badge ${colors[status as keyof typeof colors] || "badge-info"}`;
  };

  const getSubscriptionBadge = (subscription: string) => {
    return `badge ${subscription === "premium" ? "badge-info" : "badge-secondary"}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getFullName = (customer: Customer) => {
    return `${customer.first_name} ${customer.last_name}`;
  };

  // Render modal content
  const renderModalContent = () => {
    switch (modalType) {
      case "view":
        return (
          <div>
            <div className="modal-header">
              <h2>Customer Details</h2>
              <button
                onClick={closeModal}
                className="btn-secondary"
                style={{ padding: "8px", minWidth: "auto" }}
              >
                <X size={16} />
              </button>
            </div>
            <div className="modal-body">
              {selectedCustomer && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "20px",
                  }}
                >
                  <div>
                    <h3>Personal Information</h3>
                    <p>
                      <strong>Name:</strong> {getFullName(selectedCustomer)}
                    </p>
                    <p>
                      <strong>Email:</strong> {selectedCustomer.email}
                    </p>
                    <p>
                      <strong>Phone:</strong>{" "}
                      {selectedCustomer.phone || "Not provided"}
                    </p>
                    <p>
                      <strong>Address:</strong>{" "}
                      {selectedCustomer.address || "Not provided"}
                    </p>
                    <p>
                      <strong>City:</strong>{" "}
                      {selectedCustomer.parish || "Not provided"}
                    </p>
                  </div>
                  <div>
                    <h3>Account Status</h3>
                    <p>
                      <strong>Status:</strong>{" "}
                      <span className={getStatusBadge(selectedCustomer.status)}>
                        {selectedCustomer.status}
                      </span>
                    </p>
                    <p>
                      <strong>Subscription:</strong>{" "}
                      <span
                        className={getSubscriptionBadge(
                          selectedCustomer.subscription_status,
                        )}
                      >
                        {selectedCustomer.subscription_status}
                      </span>
                    </p>
                    <p>
                      <strong>Email Verified:</strong>{" "}
                      {selectedCustomer.email_verified ? "Yes" : "No"}
                    </p>
                    <p>
                      <strong>Phone Verified:</strong>{" "}
                      {selectedCustomer.phone_verified ? "Yes" : "No"}
                    </p>
                    <p>
                      <strong>Service Inquiries:</strong>{" "}
                      {selectedCustomer.total_inquiries || 0}
                    </p>
                    <p>
                      <strong>Created:</strong>{" "}
                      {formatDate(selectedCustomer.created_at)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case "create":
      case "edit":
        return (
          <div>
            <div className="modal-header">
              <h2>
                {modalType === "create" ? "Add New Customer" : "Edit Customer"}
              </h2>
              <button
                onClick={closeModal}
                className="btn-secondary"
                style={{ padding: "8px", minWidth: "auto" }}
              >
                <X size={16} />
              </button>
            </div>
            <div className="modal-body">
              <form
                onSubmit={handleSubmit}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                }}
              >
                <div>
                  <label className="form-label">First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`form-control ${formErrors.firstName ? "error" : ""}`}
                    required
                  />
                  {formErrors.firstName && (
                    <div className="error-message">{formErrors.firstName}</div>
                  )}
                </div>

                <div>
                  <label className="form-label">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`form-control ${formErrors.lastName ? "error" : ""}`}
                    required
                  />
                  {formErrors.lastName && (
                    <div className="error-message">{formErrors.lastName}</div>
                  )}
                </div>

                <div>
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`form-control ${formErrors.email ? "error" : ""}`}
                    required
                  />
                  {formErrors.email && (
                    <div className="error-message">{formErrors.email}</div>
                  )}
                </div>

                <div>
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>

                <div>
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    name="parish"
                    value={formData.parish}
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>

                <div>
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>

                {formErrors.general && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div className="error-message">{formErrors.general}</div>
                  </div>
                )}

                <div
                  style={{
                    gridColumn: "1 / -1",
                    display: "flex",
                    gap: "10px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : <Save size={16} />}{" "}
                    {modalType === "create"
                      ? "Create Customer"
                      : "Update Customer"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );

      case "delete":
        return (
          <div>
            <div className="modal-header">
              <h2>Delete Customer</h2>
              <button
                onClick={closeModal}
                className="btn-secondary"
                style={{ padding: "8px", minWidth: "auto" }}
              >
                <X size={16} />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign: "center" }}>
                <AlertTriangle
                  size={48}
                  style={{ color: "var(--danger)", marginBottom: "10px" }}
                />
                <h3>Are you sure you want to delete this customer?</h3>
                <p>
                  This action cannot be undone. The customer will be permanently
                  removed from the system.
                </p>
                {selectedCustomer && (
                  <div
                    style={{
                      background: "var(--background-light)",
                      padding: "15px",
                      borderRadius: "8px",
                      margin: "20px 0",
                    }}
                  >
                    <strong>{getFullName(selectedCustomer)}</strong>
                    <br />
                    <span style={{ color: "var(--text-medium)" }}>
                      {selectedCustomer.email}
                    </span>
                  </div>
                )}
                {formErrors.general && (
                  <div
                    className="error-message"
                    style={{ marginBottom: "10px" }}
                  >
                    {formErrors.general}
                  </div>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                }}
              >
                <button onClick={closeModal} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="btn-danger"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    "Deleting..."
                  ) : (
                    <>
                      <Trash2 size={16} /> Delete Customer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <AdminLayout
        title="Customer Management"
        subtitle="Manage and monitor customer accounts"
      >
        <Loader message="Loading customers..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Customer Management"
      subtitle="Manage and monitor customer accounts"
    >
      <div className="fade-in">
        {/* Header Actions */}
        <div className="admin-card" style={{ marginBottom: "25px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "15px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                flex: 1,
              }}
            >
              {/* Search */}
              <div style={{ position: "relative", minWidth: "300px" }}>
                <input
                  type="text"
                  placeholder="Search customers..."
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: "40px" }}
                />
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--text-medium)",
                  }}
                />
              </div>

              {/* Filters */}
              <select
                className="form-control"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ minWidth: "120px" }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="blocked">Blocked</option>
              </select>

              <select
                className="form-control"
                value={subscriptionFilter}
                onChange={(e) => setSubscriptionFilter(e.target.value)}
                style={{ minWidth: "150px" }}
              >
                <option value="all">All Subscriptions</option>
                <option value="free">Free</option>
                <option value="premium">Premium</option>
              </select>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              {selectedCustomers.length > 0 && (
                <button
                  className="btn-warning"
                  onClick={() => setShowBulkModal(true)}
                >
                  <Edit size={18} />
                  Bulk Actions ({selectedCustomers.length})
                </button>
              )}
              <button className="btn-secondary" onClick={exportCustomers}>
                <Download size={18} />
                Export
              </button>
              <button
                className="btn-primary"
                onClick={() => openModal("create")}
              >
                <Plus size={18} />
                Add Customer
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stat-cards" style={{ marginBottom: "25px" }}>
          <div className="stat-card">
            <div className="stat-icon">
              <Users size={24} />
            </div>
            <div className="stat-value">{stats.total_clients}</div>
            <div className="stat-label">Total Clients</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <CheckCircle size={24} />
            </div>
            <div className="stat-value">{stats.active_clients}</div>
            <div className="stat-label">Active Clients</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Star size={24} />
            </div>
            <div className="stat-value">{stats.inactive_clients}</div>
            <div className="stat-label">Inactive Clients</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <AlertTriangle size={24} />
            </div>
            <div className="stat-value">{stats.blocked_clients}</div>
            <div className="stat-label">Blocked Clients</div>
          </div>
        </div>

        {/* Customers Table */}
        <div className="admin-card">
          <div className="card-header">
            <div className="card-title">Customer List</div>
            <div style={{ fontSize: "14px", color: "var(--text-medium)" }}>
              Showing {filteredCustomers.length} of {pagination.totalItems}{" "}
              customers
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: "50px" }}>
                    <input
                      type="checkbox"
                      checked={
                        selectedCustomers.length === customers.length &&
                        customers.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Location</th>
                  <th>Service Inquiries</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedCustomers.includes(customer.id)}
                        onChange={() => handleSelectCustomer(customer.id)}
                      />
                    </td>
                    <td>
                      <div>
                        <div style={{ fontWeight: "500", marginBottom: "2px" }}>
                          {getFullName(customer)}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "var(--text-medium)",
                          }}
                        >
                          ID: {customer.id}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ fontSize: "14px", marginBottom: "2px" }}>
                          {customer.email}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "var(--text-medium)",
                          }}
                        >
                          {customer.phone || "No phone"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ fontSize: "14px", marginBottom: "2px" }}>
                          {customer.parish || "Not specified"}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "var(--text-medium)",
                          }}
                        >
                          {customer.address || "No address"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div
                          style={{
                            fontSize: "14px",
                            marginBottom: "2px",
                            fontWeight: "500",
                          }}
                        >
                          {customer.total_inquiries || 0}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "var(--text-medium)",
                          }}
                        >
                          inquiries
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={getStatusBadge(customer.status)}>
                        {customer.status}
                      </span>
                    </td>

                    <td style={{ fontSize: "12px" }}>
                      {formatDate(customer.created_at)}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          className="btn-secondary"
                          style={{ padding: "6px", minWidth: "auto" }}
                          onClick={() => openModal("view", customer)}
                          title="View Details"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="btn-secondary"
                          style={{ padding: "6px", minWidth: "auto" }}
                          onClick={() => openModal("edit", customer)}
                          title="Edit Customer"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="btn-danger"
                          style={{ padding: "6px", minWidth: "auto" }}
                          onClick={() => openModal("delete", customer)}
                          title="Delete Customer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "20px",
              padding: "20px 0",
            }}
          >
            <div style={{ fontSize: "14px", color: "var(--text-medium)" }}>
              Page {pagination.currentPage} of {pagination.totalPages}
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                className="btn-secondary"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={pagination.currentPage === 1}
              >
                Previous
              </button>
              <button
                className="btn-secondary"
                onClick={() =>
                  setCurrentPage((prev) =>
                    Math.min(prev + 1, pagination.totalPages),
                  )
                }
                disabled={pagination.currentPage === pagination.totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Main Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal} style={{zIndex: 9999}}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              {renderModalContent()}
            </div>
          </div>
        )}

        {/* Bulk Actions Modal */}
        {showBulkModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowBulkModal(false)}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Bulk Actions</h2>
                <button
                  onClick={() => setShowBulkModal(false)}
                  className="btn-secondary"
                  style={{ padding: "8px", minWidth: "auto" }}
                >
                  <X size={16} />
                </button>
              </div>
              <div className="modal-body">
                <p>
                  Selected {selectedCustomers.length} customers. Choose an
                  action:
                </p>
                <select
                  className="form-control"
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                  style={{ marginBottom: "20px" }}
                >
                  <option value="">Select action...</option>
                  <option value="active">Set as Active</option>
                  <option value="inactive">Set as Inactive</option>
                  <option value="blocked">Set as Blocked</option>
                </select>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => setShowBulkModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkAction}
                    className="btn-primary"
                    disabled={!bulkAction}
                  >
                    Apply Action
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CustomerManagement;
